import { useQueryClient } from "@tanstack/react-query"
import { type FC, type ReactNode, useCallback, useMemo } from "react"

import trpc, { query } from "~/utilities/trpc"

import UserContext, { type UserContextValue } from "."

const UserContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const queryClient = useQueryClient()
	const utilities = query.useUtils()

	const { data: user, refetch, error } = query.user.getCreator.useQuery()

	const reload = useCallback<UserContextValue["reload"]>(
		async options => {
			if (options?.clearSessionCache) queryClient.removeQueries({ type: "inactive" })
			await refetch()
		},
		[queryClient, refetch],
	)

	const signOut = useCallback<UserContextValue["signOut"]>(async () => {
		await queryClient.cancelQueries({ type: "active" })
		utilities.user.getCreator.setData(undefined, null)
		try {
			await trpc.user.signOut.mutate()
		} catch (error) {
			console.error(error)
			await refetch()
		}
	}, [queryClient, refetch, utilities])

	const value = useMemo<UserContextValue>(
		() => ({
			isLoading: user === undefined && !error,
			error: user === undefined ? error : null,
			user: user ?? undefined,
			reload,
			signOut,
		}),
		[user, reload, signOut, error],
	)

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export default UserContextProvider
